const ACTIVITY_EVENTS = Object.freeze([
  'user_follows',
  'post_article_likes',
  'thought_reply_likes',
  'artlist_likes',
  'post_article_comments',
  'mentions'
]);

const CHANNEL_EVENTS = Object.freeze([
  'channel_members',
  'own_channel_messages',
  'joined_channel_messages',
  'channel_mentions'
]);

const SUPPORT_EVENTS = Object.freeze(['support_one_time', 'support_recurring']);

export const NOTIFICATION_PREFERENCE_SECTIONS = Object.freeze({
  collector_activity: {
    events: ['user_follows', 'post_article_likes', 'thought_reply_likes', 'post_article_comments', 'mentions', 'artlist_likes']
  },
  collector_channels_and_chats: {
    events: ['joined_channel_messages', 'channel_mentions', 'direct_messages']
  },
  collector_purchases: {
    essential: true,
    events: ['order_messages', 'shipping_updates', 'delivery_updates', 'cancellation_refund_updates']
  },
  collector_collection: {
    events: ['artwork_likes', 'artlist_additions', 'artwork_thoughts_and_replies']
  },
  gallery_activity: {
    events: ACTIVITY_EVENTS
  },
  gallery_channels: {
    events: CHANNEL_EVENTS
  },
  gallery_support: {
    events: SUPPORT_EVENTS
  },
  gallery_artworks: {
    events: ['artwork_likes', 'artwork_thoughts', 'artwork_replies', 'artlist_additions']
  },
  gallery_sales_and_direct_support: {
    essential: true,
    events: [
      'new_orders',
      'shipping_reminders',
      'direct_chat_messages',
      'order_messages',
      'gallery_reviews',
      'artwork_claimed',
      'limited_edition_sold'
    ]
  },
  artist_activity: {
    events: ACTIVITY_EVENTS
  },
  artist_channels: {
    events: CHANNEL_EVENTS
  },
  artist_support: {
    events: SUPPORT_EVENTS
  },
  artist_artworks: {
    events: [
      'gallery_artwork_added',
      'artwork_likes',
      'artwork_thoughts',
      'artwork_replies',
      'artlist_additions',
      'gallery_artwork_removed'
    ]
  },
  artist_sales: {
    essential: true,
    events: ['original_sold', 'limited_edition_sold', 'gallery_artwork_marked_sold', 'artwork_claimed']
  }
});

const ROLE_SECTION_IDS = {
  artist: ['artist_activity', 'artist_channels', 'artist_support', 'artist_artworks', 'artist_sales'],
  collector: ['collector_activity', 'collector_channels_and_chats', 'collector_purchases', 'collector_collection'],
  gallery: ['gallery_activity', 'gallery_channels', 'gallery_support', 'gallery_artworks', 'gallery_sales_and_direct_support'],
  gallery_member: ['gallery_activity', 'gallery_channels', 'gallery_support', 'gallery_artworks', 'gallery_sales_and_direct_support']
};

export function getNotificationAudience(userSession) {
  const userType = String(userSession?.user_type || userSession?.__t || '').toLowerCase();
  if (userType === 'gallery') {
    return 'gallery';
  }
  if (userSession?.gallery_id) {
    return 'gallery_member';
  }
  return ROLE_SECTION_IDS[userType] ? userType : 'collector';
}

export function getNotificationPreferenceSections(audience) {
  const roleSectionIds = ROLE_SECTION_IDS[audience] || ROLE_SECTION_IDS.collector;
  const sectionIds = roleSectionIds;
  return sectionIds.map((id) => ({
    id,
    ...NOTIFICATION_PREFERENCE_SECTIONS[id]
  }));
}

export function normalizeNotificationPreferences(preferences, sections) {
  const preferenceSections = sections || Object.entries(NOTIFICATION_PREFERENCE_SECTIONS).map(([id, section]) => ({
    id,
    ...section
  }));

  return Object.fromEntries(
    preferenceSections.map(({ id: sectionId, events: sectionEvents }) => {
      const savedSection = preferences?.[sectionId];
      const legacyValue = typeof savedSection === 'boolean' ? savedSection : true;
      const normalizedEvents = Object.fromEntries(
        sectionEvents.map((eventId) => [
          eventId,
          typeof savedSection?.[eventId] === 'boolean' ? savedSection[eventId] : legacyValue
        ])
      );
      return [sectionId, normalizedEvents];
    })
  );
}

export function areAllSectionEventsEnabled(preferences, section) {
  return section.events.every((eventId) => preferences?.[section.id]?.[eventId] === true);
}

export function updateSectionPreferences(preferences, section, enabled) {
  return {
    ...preferences,
    [section.id]: Object.fromEntries(section.events.map((eventId) => [eventId, enabled]))
  };
}

export function updateEventPreference(preferences, sectionId, eventId, enabled) {
  return {
    ...preferences,
    [sectionId]: {
      ...preferences[sectionId],
      [eventId]: enabled
    }
  };
}
